import React, { useState, useEffect } from "react";
import { XMarkIcon, ArrowDownTrayIcon, ArrowUpTrayIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import toast from "../../utils/notify";
import { uploadLMSBulkApi } from "@/services/axios/AuthApis/AiLMSApis";
import { getFrameworkMasterListApi } from "@/services/axios/AuthApis/TrustCenterApis";
import { DEPARTMENTS_WITH_DESIGNATIONS } from "@/constants/AppConstants";

const LmsUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const [files, setFiles] = useState({
    lms_master: null,
    sections: null,
    quiz: null,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // New form fields
  const [frameworks, setFrameworks] = useState([]);
  const [frameworksLoading, setFrameworksLoading] = useState(true);
  const [formData, setFormData] = useState({
    moduleTitle: '',
    framework_ids: [],
    review_frequency: '',
    owner: [],
    description: '',
  });
  const [ownerSearch, setOwnerSearch] = useState('');
  const [frameworkSearch, setFrameworkSearch] = useState('');

  // Load frameworks on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFrameworksLoading(true);
      try {
        const res = await getFrameworkMasterListApi();
        const list = res?.frameworks || (Array.isArray(res) ? res : []);
        if (!cancelled) setFrameworks(list);
      } catch { /* ignore */ }
      finally { if (!cancelled) setFrameworksLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const REVIEW_FREQUENCIES = ['Monthly', 'Quarterly', 'Semi-Annually', 'Annually', 'As Needed'];

  const filteredFrameworks = (frameworks || []).filter((fw) => {
    if (!frameworkSearch.trim()) return true;
    const q = frameworkSearch.toLowerCase();
    return (fw.name || '').toLowerCase().includes(q);
  });

  const toggleFramework = (fwId) => {
    setFormData((prev) => {
      const ids = prev.framework_ids.includes(fwId)
        ? prev.framework_ids.filter((id) => id !== fwId)
        : [...prev.framework_ids, fwId];
      return { ...prev, framework_ids: ids };
    });
  };

  const templates = {
    lms_master: {
      title: "Sample LMS Master",
      data: {
        title: "Sample Training Module",
        description: "Brief description of the training module",
        duration: "45 min",
        category: "Awareness",
        icon: "Book",
        status: "available",
        progress: 0,
        isoAlignment: ["ISO 27001", "NIST CSF"],
        quizQuestions: 10,
      },
    },
    sections: {
      title: "Sample Sections",
      data: [
        {
          id: "section-1",
          title: "Introduction",
          targetDuration: 5,
          slides: [
            {
              slideNumber: 1,
              title: "Welcome",
              bullets: ["Point 1", "Point 2"],
              voiceOverScript: "Welcome to the training...",
              duration: 60,
            },
          ],
        },
      ],
    },
    quiz: {
      title: "Sample Quiz",
      data: [
        {
          id: "1",
          question: "Sample Question?",
          options: ["Option A", "Option B", "Option C"],
          correctAnswer: "Option A",
        },
      ],
    },
  };

  const downloadTemplate = (type) => {
    const template = templates[type];
    const element = document.createElement("a");
    const file = new Blob([JSON.stringify(template.data, null, 2)], {
      type: "application/json",
    });

    element.href = URL.createObjectURL(file);
    element.download = `${type}_template.json`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success(`Downloaded ${template.title} template`);
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    
    if (!file) {
      setFiles((prev) => ({ ...prev, [fileType]: null }));
      return;
    }

    if (!file.name.endsWith(".json")) {
      setErrors((prev) => ({
        ...prev,
        [fileType]: "Only JSON files are allowed",
      }));
      setFiles((prev) => ({ ...prev, [fileType]: null }));
      return;
    }

    setErrors((prev) => ({ ...prev, [fileType]: "" }));
    setFiles((prev) => ({ ...prev, [fileType]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!files.lms_master || !files.sections || !files.quiz) {
      toast.error("All three files are required");
      return;
    }

    if (!formData.moduleTitle.trim()) {
      toast.error("Title is required");
      return;
    }

    if (!formData.framework_ids || formData.framework_ids.length === 0) {
      toast.error("Framework is required");
      return;
    }

    setLoading(true);
    const uploadFormData = new FormData();
    uploadFormData.append("lms_master_file", files.lms_master);
    uploadFormData.append("sections_file", files.sections);
    uploadFormData.append("quiz_file", files.quiz);

    // Add form fields
    uploadFormData.append("moduleTitle", formData.moduleTitle);
    uploadFormData.append("framework_ids", JSON.stringify(formData.framework_ids));
    uploadFormData.append("review_frequency", formData.review_frequency);
    uploadFormData.append("owner", formData.owner.join(' / '));
    uploadFormData.append("description", formData.description);

    try {
      const response = await uploadLMSBulkApi(uploadFormData);

      if (response.success || response.message) {
        toast.success("LMS uploaded successfully!");
        setFiles({ lms_master: null, sections: null, quiz: null });
        setFormData({
          moduleTitle: '',
          framework_ids: [],
          review_frequency: '',
          owner: [],
          description: '',
        });
        onUploadSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Upload error:", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to upload LMS";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const toggleOwner = (role) => {
    setFormData((prev) => {
      const owners = prev.owner.includes(role)
        ? prev.owner.filter((o) => o !== role)
        : [...prev.owner, role];
      return { ...prev, owner: owners };
    });
  };

  const allOwnerRoles = DEPARTMENTS_WITH_DESIGNATIONS.flatMap((dept) =>
    dept.roles.map((r) => ({ key: r.role, role: r.role, department: dept.name, responsibility: r.responsibility }))
  );

  const filteredOwnerRoles = allOwnerRoles.filter((r) => {
    if (!ownerSearch.trim()) return true;
    const q = ownerSearch.toLowerCase();
    return r.role.toLowerCase().includes(q) || r.department.toLowerCase().includes(q);
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl dark:shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        
        {/* ── Header ── */}
        <div className="bg-gradient-to-r from-brand to-brand-dark px-6 py-4 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-white font-bold text-lg">Upload LMS Data</h3>
            <p className="text-blue-100 text-sm mt-1">Upload LMS master, sections, and quiz files</p>
          </div>
          <button 
            onClick={onClose} 
            className="text-white/80 hover:text-white transition-colors duration-200"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* ── Form body ── */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          
          {/* LMS Master Upload */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">
                1. LMS Master File <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => downloadTemplate("lms_master")}
                className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium transition-colors"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Download Template
              </button>
            </div>
            <div className="relative">
              <input
                type="file"
                accept=".json"
                onChange={(e) => handleFileChange(e, "lms_master")}
                className="hidden"
                id="lms_master_input"
              />
              <label
                htmlFor="lms_master_input"
                className={`relative border-2 border-dashed rounded-xl px-4 py-8 transition-all duration-300 cursor-pointer group flex items-center justify-center ${
                  files.lms_master
                    ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-gray-500 dark:hover:bg-gray-700"
                }`}
              >
                <div className="text-center">
                  {files.lms_master ? (
                    <div className="space-y-2">
                      <div className="flex justify-center">
                        <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-full">
                          <ArrowUpTrayIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                      </div>
                      <div>
                        <p className="text-green-700 dark:text-green-400 font-semibold text-xs">{files.lms_master.name}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-center">
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full group-hover:scale-110 transition-transform duration-200">
                          <ArrowUpTrayIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-900 dark:text-white font-semibold text-xs">Drag and drop your file</p>
                        <p className="text-gray-600 dark:text-gray-400 text-xs mt-0.5">or click to browse</p>
                      </div>
                    </div>
                  )}
                </div>
              </label>
              {errors.lms_master && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-xs text-red-700 dark:text-red-400 font-medium">{errors.lms_master}</p>
                </div>
              )}
            </div>
          </div>

          {/* Sections Upload */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">
                2. Sections File <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => downloadTemplate("sections")}
                className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium transition-colors"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Download Template
              </button>
            </div>
            <div className="relative">
              <input
                type="file"
                accept=".json"
                onChange={(e) => handleFileChange(e, "sections")}
                className="hidden"
                id="sections_input"
              />
              <label
                htmlFor="sections_input"
                className={`relative border-2 border-dashed rounded-xl px-4 py-8 transition-all duration-300 cursor-pointer group flex items-center justify-center ${
                  files.sections
                    ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-gray-500 dark:hover:bg-gray-700"
                }`}
              >
                <div className="text-center">
                  {files.sections ? (
                    <div className="space-y-2">
                      <div className="flex justify-center">
                        <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-full">
                          <ArrowUpTrayIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                      </div>
                      <div>
                        <p className="text-green-700 dark:text-green-400 font-semibold text-xs">{files.sections.name}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-center">
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full group-hover:scale-110 transition-transform duration-200">
                          <ArrowUpTrayIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-900 dark:text-white font-semibold text-xs">Drag and drop your file</p>
                        <p className="text-gray-600 dark:text-gray-400 text-xs mt-0.5">or click to browse</p>
                      </div>
                    </div>
                  )}
                </div>
              </label>
              {errors.sections && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-xs text-red-700 dark:text-red-400 font-medium">{errors.sections}</p>
                </div>
              )}
            </div>
          </div>

          {/* Quiz Upload */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">
                3. Quiz File <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => downloadTemplate("quiz")}
                className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium transition-colors"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Download Template
              </button>
            </div>
            <div className="relative">
              <input
                type="file"
                accept=".json"
                onChange={(e) => handleFileChange(e, "quiz")}
                className="hidden"
                id="quiz_input"
              />
              <label
                htmlFor="quiz_input"
                className={`relative border-2 border-dashed rounded-xl px-4 py-8 transition-all duration-300 cursor-pointer group flex items-center justify-center ${
                  files.quiz
                    ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-gray-500 dark:hover:bg-gray-700"
                }`}
              >
                <div className="text-center">
                  {files.quiz ? (
                    <div className="space-y-2">
                      <div className="flex justify-center">
                        <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-full">
                          <ArrowUpTrayIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                      </div>
                      <div>
                        <p className="text-green-700 dark:text-green-400 font-semibold text-xs">{files.quiz.name}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-center">
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full group-hover:scale-110 transition-transform duration-200">
                          <ArrowUpTrayIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-900 dark:text-white font-semibold text-xs">Drag and drop your file</p>
                        <p className="text-gray-600 dark:text-gray-400 text-xs mt-0.5">or click to browse</p>
                      </div>
                    </div>
                  )}
                </div>
              </label>
              {errors.quiz && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-xs text-red-700 dark:text-red-400 font-medium">{errors.quiz}</p>
                </div>
              )}
            </div>
          </div>

          {/* Form Fields Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6 space-y-5">
            <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">Module Information</h4>

            {/* Title */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.moduleTitle}
                onChange={handleFormChange('moduleTitle')}
                placeholder="Enter training module title"
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Framework */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Framework <span className="text-red-500">*</span>
                <span className="text-gray-400 font-normal">({formData.framework_ids.length} selected)</span>
              </label>

              {formData.framework_ids.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {formData.framework_ids.map((fwId) => {
                    const fw = frameworks.find((f) => f.id === fwId);
                    return (
                      <span
                        key={fwId}
                        className="inline-flex items-center gap-1 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-full px-2.5 py-1"
                      >
                        <span className="font-semibold">{fw?.name || fwId}</span>
                        <button
                          type="button"
                          onClick={() => toggleFramework(fwId)}
                          className="ml-0.5 text-blue-400 dark:text-blue-500 hover:text-red-500 transition-colors"
                        >
                          <XMarkIcon className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 shrink-0" />
                  <input
                    type="text"
                    placeholder="Search frameworks…"
                    value={frameworkSearch}
                    onChange={(e) => setFrameworkSearch(e.target.value)}
                    className="flex-1 text-xs bg-transparent outline-none dark:text-white"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredFrameworks.length === 0 ? (
                    <p className="text-xs text-gray-400 py-3 text-center">No matching frameworks.</p>
                  ) : (
                    filteredFrameworks.map((fw) => {
                      const checked = formData.framework_ids.includes(fw.id);
                      return (
                        <label
                          key={fw.id}
                          className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors ${
                            checked ? 'bg-blue-50/60 dark:bg-blue-900/20' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleFramework(fw.id)}
                            className="mt-0.5 rounded border-gray-300 text-blue-600 dark:border-gray-600 focus:ring-blue-500"
                          />
                          <div className="min-w-0">
                            <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">{fw.name}</span>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Review Frequency */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Review Frequency
              </label>
              <select
                value={formData.review_frequency}
                onChange={handleFormChange('review_frequency')}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select</option>
                {REVIEW_FREQUENCIES.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>

            {/* Owner */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Owner <span className="text-gray-400 font-normal">({formData.owner.length} selected)</span>
              </label>

              {formData.owner.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {formData.owner.map((role) => (
                    <span
                      key={role}
                      className="inline-flex items-center gap-1 text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 rounded-full px-2.5 py-1"
                    >
                      <span className="font-semibold">{role}</span>
                      <button
                        type="button"
                        onClick={() => toggleOwner(role)}
                        className="ml-0.5 text-green-400 dark:text-green-500 hover:text-red-500 transition-colors"
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 shrink-0" />
                  <input
                    type="text"
                    placeholder="Search departments or roles…"
                    value={ownerSearch}
                    onChange={(e) => setOwnerSearch(e.target.value)}
                    className="flex-1 text-xs bg-transparent outline-none dark:text-white"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredOwnerRoles.length === 0 ? (
                    <p className="text-xs text-gray-400 py-3 text-center">No matching roles.</p>
                  ) : (
                    filteredOwnerRoles.map((r) => {
                      const checked = formData.owner.includes(r.role);
                      return (
                        <label
                          key={`${r.department}-${r.role}`}
                          className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors ${
                            checked ? 'bg-green-50/60 dark:bg-green-900/20' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOwner(r.role)}
                            className="mt-0.5 rounded border-gray-300 text-green-600 dark:border-gray-600 focus:ring-green-500"
                          />
                          <div className="min-w-0">
                            <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">{r.role}</span>
                            <span className="ml-2 text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded">
                              {r.department}
                            </span>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                rows={2}
                value={formData.description}
                onChange={handleFormChange('description')}
                placeholder="Enter module description"
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Instructions */}
          <div className="mt-6 p-4 bg-slate-50 dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-gray-700">
            <p className="text-xs text-slate-700 dark:text-gray-300 font-semibold mb-2">File Format:</p>
            <ul className="text-xs text-slate-600 dark:text-gray-400 space-y-1 list-disc list-inside">
              <li>Only JSON files are accepted</li>
              <li>All three files are required (LMS Master, Sections, Quiz)</li>
              <li>Download templates for correct structure</li>
            </ul>
          </div>

          {/* ── Footer Buttons ── */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end gap-3 shrink-0 -mx-6 -mb-6">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !files.lms_master || !files.sections || !files.quiz}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 inline-flex items-center gap-2"
            >
              {loading && (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              )}
              {loading ? "Uploading..." : "Upload LMS"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LmsUploadModal;
